import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedDate, FormattedTime, FormattedMessage } from "react-intl";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isPast,
  startOfDay,
  format,
  isToday,
} from "date-fns";
import { darken } from "polished";

import { ApiClient } from "../../utils/Api";
import { Button, Icon, Heading5, Paragraph, Row } from "../";
import TimeSlotPicker from "./TimeSlotPicker";

/* ─── Layout: calendar + selected-list side-by-side on desktop ─────────── */

const Wrapper = styled.div`
  margin: 1rem 0;
`;

const ContentRow = styled(Row)`
  flex-direction: column;
  gap: 1.5rem;

  @media (${props => props.theme.tabletScreen}) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const CalendarSection = styled.div`
  flex: 1;

  @media (${props => props.theme.tabletScreen}) {
    min-width: 320px;
  }
`;

/* ─── Calendar header / grid ────────────────────────────────────────────── */

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const MonthTitle = styled.h2`
  font-family: "Quicksand";
  font-weight: 400;
  font-size: 1.25rem;
  margin: 0;
  color: ${props => props.theme.gray900};
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 1rem;
`;

const DayLabel = styled.div`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.tiny};
  color: ${props => props.theme.gray600};
  padding: 0.25rem 0;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const DayCell = styled.button`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  font-size: ${props => props.theme.fontSizes.small};
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  transition: background-color 0.2s, color 0.2s;
  padding: 0;
  min-height: 2.2rem;

  @media (${props => props.theme.tabletScreen}) {
    max-width: 2.5rem;
    max-height: 2.5rem;
  }

  background-color: ${props => {
    if (props.selected) return props.theme.info;
    if (props.hasSlot && !props.outside) return props.theme.success;
    if (props.today && !props.outside) return props.theme.primary;
    return "transparent";
  }};

  color: ${props => {
    if (props.selected || props.hasSlot || (props.today && !props.outside))
      return props.theme.white;
    if (props.outside || props.disabled) return props.theme.gray400;
    return props.theme.gray900;
  }};

  font-weight: ${props => (props.today || props.selected || props.hasSlot ? "600" : "400")};

  &:hover:not(:disabled):not([disabled]) {
    background-color: ${props =>
      props.selected ? darken(0.05, props.theme.info) : props.theme.gray200};
    color: ${props => (props.selected ? props.theme.white : props.theme.gray900)};
  }
`;

const MaxReachedMessage = styled(Paragraph)`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.warning};
  margin-top: 0.75rem;
  margin-bottom: 0;
`;

/* ─── Selected slots panel ──────────────────────────────────────────────── */

const SelectedList = styled.div`
  flex: 1;
  width: 100%;
  padding: 1rem;
  background-color: ${props => props.theme.gray100};
  border-radius: ${props => props.theme.borderRadius};
  align-self: flex-start;

  @media (${props => props.theme.tabletScreen}) {
    min-width: 320px;
    width: auto;
  }
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px solid ${props => props.theme.gray200};
  font-size: ${props => props.theme.fontSizes.small};
  &:last-child {
    border-bottom: none;
  }
`;

const SlotUserName = styled.span`
  font-size: ${props => props.theme.fontSizes.tiny};
  color: ${props => props.theme.gray600};
  margin-left: 0.25rem;
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: ${props => props.theme.gray600};
  padding: 0 0.25rem;
  font-size: 1rem;
  line-height: 1;
  &:hover {
    color: ${props => props.theme.danger};
  }
`;

const ProgressInfo = styled(Paragraph)`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.gray600};
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const EmptyHint = styled(Paragraph)`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.gray500};
  margin-bottom: 0;
`;

const DAY_LABEL_KEYS = [
  "components.CalendarPicker.day.mo",
  "components.CalendarPicker.day.tu",
  "components.CalendarPicker.day.we",
  "components.CalendarPicker.day.th",
  "components.CalendarPicker.day.fr",
  "components.CalendarPicker.day.sa",
  "components.CalendarPicker.day.su",
];

const CalendarPicker = ({ provider, event, value, onChange, max }) => {
  const selectedSlots = value || [];

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const isMaxReached = selectedSlots.length >= max;
  // Single-slot pickers always allow re-selection (replacing the prior slot)
  const blocksFurtherPicks = isMaxReached && max !== 1;

  const handleDateClick = useCallback(
    async date => {
      if (blocksFurtherPicks) return;

      setSelectedDate(date);
      setPopoverOpen(true);
      setLoadingSlots(true);
      setAvailabilities([]);
      setSlotsError(false);

      try {
        const dateStr = format(date, "yyyy-MM-dd");
        const data = await new ApiClient().providerAvailabilities(
          provider.id,
          dateStr,
          event.id,
        );
        setAvailabilities((data && data.availabilities) || []);
      } catch (e) {
        setSlotsError(true);
        setAvailabilities([]);
      } finally {
        setLoadingSlots(false);
      }
    },
    [provider.id, event.id, blocksFurtherPicks],
  );

  const handleSlotSelect = useCallback(
    slot => {
      if (!selectedDate) return;
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const startDate = `${dateStr}T${slot.fromTime.length === 5 ? `${slot.fromTime}:00` : slot.fromTime}`;
      const endDate = `${dateStr}T${slot.toTime.length === 5 ? `${slot.toTime}:00` : slot.toTime}`;
      const userId = slot.userId || null;

      const alreadySelected = selectedSlots.some(
        s => s.startDate === startDate && s.endDate === endDate && s.userId === userId,
      );

      if (!alreadySelected) {
        const next =
          max === 1
            ? [{ startDate, endDate, userName: slot.userName || null, userId }]
            : [
                ...selectedSlots,
                { startDate, endDate, userName: slot.userName || null, userId },
              ];

        if (max === 1 || selectedSlots.length < max) {
          onChange(next);
        }
      }

      setSelectedDate(null);
      setPopoverOpen(false);
    },
    [selectedDate, selectedSlots, max, onChange],
  );

  const handleRemoveSlot = useCallback(
    slot => {
      onChange(
        selectedSlots.filter(
          s =>
            !(
              s.startDate === slot.startDate &&
              s.endDate === slot.endDate &&
              s.userId === slot.userId
            ),
        ),
      );
    },
    [selectedSlots, onChange],
  );

  const buildCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  const calendarDays = buildCalendarDays();
  const isPrevMonthPast = isPast(
    startOfDay(addDays(endOfMonth(subMonths(currentMonth, 1)), 1)),
  );

  return (
    <Wrapper>
      <ContentRow>
        <CalendarSection>
          <CalendarHeader>
            <Button
              color="default"
              glow={false}
              size="small"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              disabled={isPrevMonthPast}
            >
              <Icon name="chevron-up" direction="left" />
            </Button>
            <MonthTitle>{format(currentMonth, "MMMM yyyy")}</MonthTitle>
            <Button
              color="default"
              glow={false}
              size="small"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <Icon name="chevron-up" direction="right" />
            </Button>
          </CalendarHeader>

          <CalendarGrid>
            {DAY_LABEL_KEYS.map(key => (
              <DayLabel key={key}>
                <FormattedMessage id={key} />
              </DayLabel>
            ))}
            {calendarDays.map(day => {
              const isOutside = !isSameMonth(day, currentMonth);
              const isPastDay = isPast(startOfDay(day)) && !isToday(day);
              const isDisabled = isOutside || isPastDay || blocksFurtherPicks;
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const today = isToday(day);
              const hasSlot = selectedSlots.some(
                s => s.startDate && isSameDay(new Date(s.startDate), day),
              );

              return (
                <DayCell
                  key={day.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  selected={isSelected}
                  outside={isOutside}
                  today={today}
                  hasSlot={hasSlot && !isOutside}
                  onClick={() => !isDisabled && handleDateClick(day)}
                  aria-label={format(day, "yyyy-MM-dd")}
                  aria-pressed={isSelected}
                >
                  {format(day, "d")}
                </DayCell>
              );
            })}
          </CalendarGrid>

          {blocksFurtherPicks && (
            <MaxReachedMessage>
              <FormattedMessage id="components.CalendarPicker.maxReached" />
            </MaxReachedMessage>
          )}
        </CalendarSection>

        <SelectedList>
          <Heading5>
            <FormattedMessage id="components.CalendarPicker.selectedSlots" />
          </Heading5>
          {max > 1 && (
            <ProgressInfo>
              <FormattedMessage
                id="components.CalendarPicker.selectSlots"
                values={{
                  selected: selectedSlots.length,
                  required: max,
                }}
              />
            </ProgressInfo>
          )}
          {selectedSlots.length === 0 && (
            <EmptyHint>
              <FormattedMessage id="components.CalendarPicker.noneSelected" />
            </EmptyHint>
          )}
          {selectedSlots.map(slot => (
            <SelectedItem key={slot.startDate}>
              <span>
                <Icon name="Calendar" spaced="right" />
                <FormattedDate value={new Date(slot.startDate)} month="short" day="2-digit" />
                {", "}
                <FormattedTime value={new Date(slot.startDate)} hour="2-digit" minute="2-digit" />
                {" – "}
                <FormattedTime value={new Date(slot.endDate)} hour="2-digit" minute="2-digit" />
                {slot.userName && <SlotUserName>({slot.userName})</SlotUserName>}
              </span>
              <RemoveButton
                type="button"
                onClick={() => handleRemoveSlot(slot)}
                aria-label="Remove"
              >
                <Icon name="trash" size="small" />
              </RemoveButton>
            </SelectedItem>
          ))}
        </SelectedList>
      </ContentRow>

      <TimeSlotPicker
        isOpen={popoverOpen}
        selectedDate={selectedDate}
        availabilities={availabilities}
        loadingSlots={loadingSlots}
        slotsError={slotsError}
        onClose={() => setPopoverOpen(false)}
        onSlotSelect={handleSlotSelect}
      />
    </Wrapper>
  );
};

CalendarPicker.propTypes = {
  provider: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number,
};

CalendarPicker.defaultProps = {
  value: [],
  max: 1,
};

export default CalendarPicker;
